import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, AlertCircle, FileText, Download, Loader2 } from "lucide-react";
import { authenticatedFetch } from "./config/api";
import { generateAndDownloadPdf } from "./utils/pdfGenerator";
import { invoiceTranslations } from "./translations/invoiceTranslations";
import { Helmet } from "react-helmet-async";

export default function SharedInvoiceViewer() {
  const { token } = useParams<{ token: string }>();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState<any>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/public/view-invoice", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });

      if (response.ok) {
        const data = await response.json();
        setInvoiceData(data);
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Invalid password or link expired");
      }
    } catch (err) {
      setError("Connection error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!invoiceData) return;
    setIsGeneratingPdf(true);
    try {
      const { draft, designSettings, language } = invoiceData;
      const lang = (language || "cs") as keyof typeof invoiceTranslations;
      const translations = invoiceTranslations[lang] || invoiceTranslations.cs;

      await generateAndDownloadPdf({
        draft,
        translations,
        designSettings,
        fontFamily: designSettings?.fontFamily || "Inter, Arial, sans-serif",
      });
    } catch (err) {
      console.error(err);
      alert("Error generating PDF");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  if (invoiceData) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col items-center justify-center p-4">
        <Helmet>
          <title>View Invoice | Invoitio</title>
          <meta name="robots" content="noindex, nofollow, noarchive" />
        </Helmet>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-800 rounded-[2.5rem] shadow-2xl p-8 md:p-12 max-w-lg w-full text-center border border-slate-200 dark:border-slate-700"
        >
          <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-3xl flex items-center justify-center mx-auto text-blue-600 dark:text-blue-400 mb-8">
            <FileText size={40} />
          </div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white mb-4 uppercase tracking-tight">
            Invoice #{invoiceData.draft.invoiceNo}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 font-medium mb-8">
            The invoice has been successfully verified. You can now download it as a PDF.
          </p>
          
          <button
            onClick={handleDownloadPdf}
            disabled={isGeneratingPdf}
            className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black shadow-lg shadow-indigo-500/25 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
          >
            {isGeneratingPdf ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Download className="w-5 h-5" />
            )}
            <span>Download PDF</span>
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4 font-inter">
      <Helmet>
        <title>Protected Invoice | Invoitio</title>
        <meta name="robots" content="noindex, nofollow, noarchive" />
      </Helmet>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white dark:bg-slate-800 rounded-[2.5rem] shadow-2xl p-8 md:p-12 max-w-md w-full border border-slate-200 dark:border-slate-700"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-2xl flex items-center justify-center mx-auto text-emerald-600 dark:text-emerald-400 mb-6 shadow-inner">
            <Lock size={32} />
          </div>
          <h1 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">
            Protected Invoice
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-2 font-medium">
            This document is password protected
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-1">
              Access Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full px-5 py-4 rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all text-center font-bold tracking-widest"
              autoFocus
            />
          </div>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 p-4 rounded-xl text-xs font-bold flex items-center gap-2 border border-rose-100 dark:border-rose-900/30"
              >
                <AlertCircle size={16} />
                <span>{error}</span>
              </motion.div>
            )}
          </AnimatePresence>

          <button
            type="submit"
            disabled={isLoading || !password}
            className="w-full py-4 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-2xl font-black shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-50 disabled:hover:scale-100"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              "View Document"
            )}
          </button>
        </form>

        <div className="mt-12 pt-8 border-t border-slate-100 dark:border-slate-700 text-center">
          <p className="text-sm text-slate-600 dark:text-slate-400 font-medium mb-4">
            Create invoices in 10 seconds.
          </p>
          <a
            href="https://invoitio.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black text-xs transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-indigo-500/20"
          >
            Try Invoitio free for 30 days. No commitment.
          </a>
        </div>
      </motion.div>
    </div>
  );
}

